import { useLingui } from '@lingui/react/macro';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@repo/videa-components/src/ui/select';
import { Switch } from '@repo/videa-components/src/ui/switch';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@repo/videa-components/src/ui/tooltip';
import { useFormContext, useWatch } from 'react-hook-form';

import {
  usePracticesBySpecialty,
  useSpecialistsByPractice,
  useSpecialties,
} from '@/react-query/useReferralInfo';

import type { ReferralFormValues } from '../../types';

interface TreatingSpecialistTabProps {
  onPracticeSelect?: (
    practice:
      | {
          practice_id: number;
          practice_name: string;
          referral_practice_address?: string;
          referral_practice_phone?: string;
        }
      | undefined,
  ) => void;
  videaPracticeId: string;
}

export const TreatingSpecialistTab = ({
  onPracticeSelect,
  videaPracticeId,
}: TreatingSpecialistTabProps) => {
  const { t } = useLingui();
  const {
    setValue,
    formState: { errors },
  } = useFormContext<ReferralFormValues>();

  // Watch the selected values to fetch dependent data
  const selectedSpecialtyId = useWatch({ name: 'specialtyId' });
  const selectedPracticeId = useWatch({ name: 'referralPracticeId' });
  const useNextAvailableSpecialist = useWatch({
    name: 'useNextAvailableSpecialist',
  });
  const selectedSpecialistId = useWatch({ name: 'referralSpecialistId' });

  // Fetch specialties, procedures, practices, and specialists
  const { data: specialties, isLoading: isSpecialtiesLoading } =
    useSpecialties(videaPracticeId);
  const { data: practices, isLoading: isPracticesLoading } =
    usePracticesBySpecialty(selectedSpecialtyId || 0, {
      enabled: !!selectedSpecialtyId,
    });
  const { data: specialists, isLoading: isSpecialistsLoading } =
    useSpecialistsByPractice(selectedPracticeId || 0, {
      enabled: !!selectedPracticeId,
    });

  const handleSpecialtyChange = (specialtyId: string) => {
    setValue('specialtyId', parseInt(specialtyId, 10));
    setValue('proceduresIds', []);
    setValue('referralPracticeId', undefined);
    setValue('referralSpecialistId', undefined);
    setValue('useNextAvailableSpecialist', false);
    setValue('referralPracticeAddress', '');
    setValue('referralPracticePhone', '');
    onPracticeSelect?.(undefined);
  };

  const handlePracticeChange = (practiceId: string) => {
    const selectedPractice = practices?.find(
      (practice) => practice.practice_id.toString() === practiceId,
    );

    setValue('referralPracticeId', parseInt(practiceId, 10));
    setValue('referralSpecialistId', undefined);
    setValue('useNextAvailableSpecialist', false);

    // Store the practice address and phone if available
    if (selectedPractice) {
      setValue(
        'referralPracticeAddress',
        selectedPractice.referral_practice_address || '',
      );
      setValue(
        'referralPracticePhone',
        selectedPractice.referral_practice_phone || '',
      );
      onPracticeSelect?.(selectedPractice);
    } else {
      setValue('referralPracticeAddress', '');
      setValue('referralPracticePhone', '');
      onPracticeSelect?.(undefined);
    }
  };

  const handleSpecialistChange = (specialistId: string) => {
    setValue('referralSpecialistId', parseInt(specialistId, 10));
  };

  const handleNextAvailableToggle = (checked: boolean) => {
    setValue('useNextAvailableSpecialist', checked);
    if (checked) {
      // Clear specialist selection when switching to next available
      setValue('referralSpecialistId', undefined);
    }
  };

  return (
    <div className="vai-space-y-4">
      <div className="vai-grid vai-grid-cols-1 vai-gap-10 sm:vai-grid-cols-2">
        <div className="vai-space-y-1">
          <label className="vai-text-sm vai-font-medium">
            {t`Specialty`}
            <span className="vai-text-red-500">*</span>
          </label>
          <Select
            value={selectedSpecialtyId?.toString() || ''}
            onValueChange={handleSpecialtyChange}
          >
            <SelectTrigger>
              <SelectValue
                placeholder={
                  isSpecialtiesLoading
                    ? t`Loading specialties...`
                    : t`Select specialty`
                }
              />
            </SelectTrigger>
            <SelectContent side="bottom" align="start" className="vai-max-h-48">
              {specialties?.map((specialty) => (
                <SelectItem
                  key={specialty.id}
                  value={specialty.id.toString()}
                  className="vai-cursor-pointer hover:vai-bg-gray-100 vai-transition-colors"
                >
                  {specialty.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          {errors.specialtyId && (
            <p className="vai-text-red-500 vai-text-sm">
              {errors.specialtyId.message}
            </p>
          )}
          {/* API errors are handled by the enabled condition in the hooks */}
        </div>

        <div className="vai-space-y-1">
          <label className="vai-text-sm vai-font-medium">
            {t`Practice`}
            <span className="vai-text-red-500">*</span>
          </label>
          <Select
            value={selectedPracticeId?.toString() || ''}
            onValueChange={handlePracticeChange}
            disabled={!selectedSpecialtyId}
          >
            <SelectTrigger>
              <SelectValue
                placeholder={
                  !selectedSpecialtyId
                    ? t`Select specialty first`
                    : isPracticesLoading
                      ? t`Loading practices...`
                      : t`Select practice`
                }
              />
            </SelectTrigger>
            <SelectContent side="bottom" align="start" className="vai-max-h-48">
              {practices?.map((practice) => (
                <SelectItem
                  key={practice.practice_name}
                  value={practice.practice_id.toString()}
                  className="vai-cursor-pointer hover:vai-bg-gray-100 vai-transition-colors"
                >
                  {practice.practice_name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          {errors.referralPracticeId && (
            <p className="vai-text-red-500 vai-text-sm">
              {errors.referralPracticeId.message}
            </p>
          )}
        </div>

        <div
          className={
            useNextAvailableSpecialist ? 'vai-space-y-2' : 'vai-space-y-2'
          }
        >
          <div className="vai-flex vai-items-center vai-justify-between">
            <label className="vai-text-sm vai-font-medium">
              {t`Specialist`}
              <span className="vai-text-red-500">*</span>
            </label>

            {/* Next Available Specialist Toggle with Tooltip */}
            <Tooltip>
              <TooltipTrigger asChild>
                <div className="vai-flex vai-items-center vai-space-x-2">
                  <Switch
                    checked={useNextAvailableSpecialist || false}
                    onCheckedChange={handleNextAvailableToggle}
                    disabled={!selectedPracticeId}
                  />
                  <span className="vai-text-sm vai-text-gray-700">
                    {t`Next Available`}
                  </span>
                </div>
              </TooltipTrigger>
              <TooltipContent>
                <p className="vai-max-w-xs">
                  {t`Faster scheduling - the specialist will assign the next available clinician at chosen practice`}
                </p>
              </TooltipContent>
            </Tooltip>
          </div>

          {/* Specialist Selection - Conditional based on toggle */}
          {useNextAvailableSpecialist ? (
            <div className="vai-p-3 vai-bg-green-50 vai-border vai-border-green-200 vai-rounded-md">
              <p className="vai-text-sm vai-text-green-800">
                {t`The specialist clinic will schedule your patient with the next available provider.`}
              </p>
            </div>
          ) : (
            <div className="vai-space-y-1">
              <Select
                value={selectedSpecialistId?.toString() || ''}
                onValueChange={handleSpecialistChange}
                disabled={!selectedPracticeId}
              >
                <SelectTrigger>
                  <SelectValue
                    placeholder={
                      !selectedPracticeId
                        ? t`Select practice first`
                        : isSpecialistsLoading
                          ? t`Loading specialists...`
                          : t`Select specialist`
                    }
                  />
                </SelectTrigger>
                <SelectContent
                  side="bottom"
                  align="start"
                  className="vai-max-h-48"
                >
                  {specialists
                    ?.filter(
                      (specialist) =>
                        specialist.specialty_id === selectedSpecialtyId,
                    )
                    .map((specialist) => (
                      <SelectItem
                        key={specialist.specialist_name}
                        value={specialist.specialist_id.toString()}
                        className="vai-cursor-pointer hover:vai-bg-gray-100 vai-transition-colors"
                      >
                        {specialist.specialist_name}
                      </SelectItem>
                    ))}
                </SelectContent>
              </Select>
              {errors.referralSpecialistId && (
                <p className="vai-text-red-500 vai-text-sm">
                  {errors.referralSpecialistId.message}
                </p>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
